import { Button, Link, Stack, Typography } from "@mui/material"
import { getAuth, signInWithEmailAndPassword } from "firebase/auth"
import { useSnackbar } from "notistack"
import type { FC } from "react"
import { useEffect, useState } from "react"
import { HomeAppBar } from "app/components/HomeAppBar"
import { HomeLoginForm } from "app/components/HomeLoginForm"
import SSPaper from "app/components/SSPaper"
import { useLoginWithTokenMutation } from "interface/__generated__/react"
import type { OnLoginWithEmail } from "interface/types/onLoginWithEmail"
import { t } from "lib/i18n";

type Props = {
  resetError(): void
}

export const HomeLogin: FC<Props> = (props) => {
  const [loginWithToken] = useLoginWithTokenMutation()

  const [isLoading, setLoading] = useState(false)

  const { enqueueSnackbar } = useSnackbar()

  // セッション切れからのリダイレクト時に通知を表示
  useEffect(() => {
    if (typeof window !== 'undefined' && sessionStorage.getItem('sessionExpired')) {
      sessionStorage.removeItem('sessionExpired')
      enqueueSnackbar(t('Your session has expired. Please sign in again.'), { variant: 'info' })
    }
  }, [enqueueSnackbar])

  const onLogin: OnLoginWithEmail = async (form) => {
    try {
      setLoading(true)
      const result = await signInWithEmailAndPassword(
        getAuth(),
        form.email,
        form.password,
      )
      const token = await result.user.getIdToken()
      await loginWithToken({
        variables: {
          input: { token, password: form.password },
        },
      })
      props.resetError()
    } catch (error) {
      setLoading(false)

      if (error instanceof Error) {
        // Error message available for debugging

        if (error.message.includes("auth/user-not-found")) {
          enqueueSnackbar(t("User not found"), { variant: "error" })
        } else if (error.message.includes("auth/wrong-password")) {
          enqueueSnackbar(t("Wrong password"), { variant: "error" })
        } else if (error.message.includes("auth/too-many-requests")) {
          enqueueSnackbar(
            "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.",
            { variant: "error" },
          )
        } else {
          enqueueSnackbar(t("Failed to login"), { variant: "error" })
        }
      } else {
        enqueueSnackbar(t("Failed to login"), { variant: "error" })
      }
    }
  }

  return (
    <Stack>
      <HomeAppBar>
        <Link href="/register">
          <Button>{t("Sign up")}</Button>
        </Link>
      </HomeAppBar>
      <Stack sx={{ px: 4, pt: 8 }} direction={"row"} justifyContent={"center"}>
        <Stack spacing={2} sx={{ width: "100%", maxWidth: 360 }}>
          <SSPaper sx={{ borderRadius: 3 }}>
            <Stack
              spacing={4}
              sx={{
                px: 4,
                pt: 4,
                pb: 5,
                width: "100%",
              }}
            >
              <Stack direction={"row"} justifyContent={"center"}>
                <Typography sx={{ fontSize: 24, fontWeight: "bold" }}>
                  {t("Sign in")}
                </Typography>
              </Stack>
              <HomeLoginForm isLoading={isLoading} onLogin={onLogin} />
              <Stack direction={"row"} justifyContent={"center"}>
                <Link href="/magic">
                  <Button size="small" sx={{ textTransform: "none" }}>
                    {t("Sign in with an email link instead")}
                  </Button>
                </Link>
              </Stack>
            </Stack>
          </SSPaper>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {`© ${new Date().getFullYear()} ShiftSmart`}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}
